import { CameraIcon, XMarkIcon } from "@heroicons/react/24/outline";
import jsQR from "jsqr";
import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Full-screen camera QR/barcode scanner for capturing an EPC, ported from
 * gen5-pwa's SingleTagQRScannerModal (jsQR + getUserMedia + a requestAnimation-
 * Frame decode loop over the centre of the frame). Differences from the legacy
 * version: Tailwind chrome instead of inline styles, and MTTR's EPC shape
 * (alphanumeric, 1–30 chars) rather than the fixed 12-char tag.
 *
 * Emits the decoded EPC via `onEpc` and closes — the page shows the lookup
 * result, which doubles as the confirmation step.
 */
interface CameraEpcModalProps {
  open: boolean;
  onClose: () => void;
  onEpc: (epc: string) => void;
  title?: string;
}

const EPC_RE = /^[A-Za-z0-9]{1,30}$/;

export const CameraEpcModal: React.FC<CameraEpcModalProps> = ({
  open,
  onClose,
  onEpc,
  title = "Scan tag",
}) => {
  const [error, setError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | undefined>(undefined);
  // Latch so a single decode doesn't fire onEpc repeatedly across frames.
  const doneRef = useRef(false);

  const stopCamera = useCallback(() => {
    setCameraReady(false);
    if (rafRef.current !== undefined) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const scanFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (doneRef.current || !video || !canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scanFrame);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Decode a central square (faster + avoids edge noise).
    const size = Math.min(canvas.width, canvas.height) * 0.5;
    const x = Math.max(0, Math.floor(canvas.width / 2 - size / 2));
    const y = Math.max(0, Math.floor(canvas.height / 2 - size / 2));
    const w = Math.min(Math.ceil(size), canvas.width - x);
    const h = Math.min(Math.ceil(size), canvas.height - y);

    try {
      const img = ctx.getImageData(x, y, w, h);
      const code = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" });
      if (code && code.data) {
        const epc = code.data.trim().toUpperCase();
        if (EPC_RE.test(epc)) {
          doneRef.current = true;
          if ("vibrate" in navigator) navigator.vibrate(80);
          onEpc(epc);
          onClose();
          return;
        }
      }
    } catch {
      /* transient getImageData/decoding hiccup — keep scanning */
    }
    rafRef.current = requestAnimationFrame(scanFrame);
  }, [onEpc, onClose]);

  // Start the camera + decode loop while open; tear everything down on close.
  useEffect(() => {
    if (!open) return;
    doneRef.current = false;
    setError(null);
    let cancelled = false;

    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.onloadedmetadata = () => {
          void video.play().finally(() => {
            setCameraReady(true);
            rafRef.current = requestAnimationFrame(scanFrame);
          });
        };
      })
      .catch(() => {
        if (!cancelled) setError("Unable to access camera. Check browser permissions.");
      });

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [open, scanFrame, stopCamera]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 h-14 bg-white border-b border-secondary-200 shrink-0">
        <h3 className="text-base font-semibold text-secondary-900">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-2 text-secondary-500 hover:text-secondary-900"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 w-full h-full object-cover"
          style={{ display: cameraReady ? "block" : "none" }}
        />
        <canvas ref={canvasRef} className="hidden" />

        {cameraReady && (
          // Centre target — matches the 50%-of-frame decode region.
          <div className="relative w-[55vw] max-w-[260px] aspect-square rounded-2xl border-4 border-primary-400 shadow-[0_0_0_2000px_rgba(0,0,0,0.45)]" />
        )}

        {!cameraReady && !error && (
          <div className="text-center text-secondary-200">
            <CameraIcon className="h-12 w-12 mx-auto mb-2 opacity-70" />
            <p className="text-sm">Starting camera…</p>
          </div>
        )}

        {error && (
          <div className="mx-4 max-w-sm text-sm text-error-700 bg-white px-3 py-2 rounded">
            {error}
          </div>
        )}

        {cameraReady && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full">
            Center the tag's QR / barcode in the box
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraEpcModal;
